import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complaintsApi, type LiteOperator, type MaterialRow, type MaterialLine } from '../api/complaints';
import { useSession } from '../auth/session';
import { useAsync, Banner, Empty } from './admin/ui';
import { Modal } from '../components/Modal';
import { ago } from '../lib/statusUi';
import { fmtDateTime } from '../lib/format';
import BottomNav from '../components/BottomNav';

type Row = Record<string, unknown>;

const STATUS_LABEL: Record<string, string> = { open: 'Open', in_progress: 'In progress', paused: 'Paused', resolved: 'Resolved' };
const STATUS_TONE: Record<string, string> = { open: 'warn', in_progress: 'ok', paused: 'warn', resolved: 'muted' };
type StatusFilter = 'all' | 'open' | 'in_progress' | 'paused' | 'resolved';

const materialsUsedLines = (r: Row): MaterialLine[] => (Array.isArray(r['Materials Used']) ? r['Materials Used'] as MaterialLine[] : []);
const condemnedLines = (r: Row): MaterialLine[] => (Array.isArray(r['Condemned Items']) ? r['Condemned Items'] as MaterialLine[] : []);
const fmtMaterialLines = (lines: MaterialLine[]) => lines.map((l) => l.item + (l.qty !== '' && l.qty != null ? ` × ${l.qty}` : '')).join(', ');

/** Native share sheet (or a clipboard-copy fallback) for one complaint's summary — so a card
 * can be forwarded to WhatsApp/email/etc. without retyping it. */
async function shareComplaint(r: Row, id: string): Promise<string> {
  const ticketNo = r['Source'] === 'external' ? ` (Ticket #${r['Source Ticket No']})` : '';
  const where = [r['Department'], r['Block'] ? `Block ${r['Block']}` : '', r['Location']].filter(Boolean).join(' · ');
  const lines = [
    `Complaint #${id}${ticketNo}`,
    where,
    String(r['Details'] ?? ''),
    `Status: ${STATUS_LABEL[String(r['Status'])] ?? String(r['Status'])}`,
  ];
  if (r['Assigned To Name']) lines.push(`Assigned to: ${String(r['Assigned To Name'])}`);
  if (materialsUsedLines(r).length) lines.push(`Used: ${fmtMaterialLines(materialsUsedLines(r))}`);
  if (condemnedLines(r).length) lines.push(`Condemned: ${fmtMaterialLines(condemnedLines(r))}`);
  const text = lines.filter(Boolean).join('\n');

  const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
  if (nav.share) {
    await nav.share({ title: `Complaint #${id}`, text });
    return 'shared';
  }
  await navigator.clipboard.writeText(text);
  return 'copied';
}

/** Shared complaints board — operators and admins see the same list and take the same actions;
 * admins additionally get a manual "+ Add" (phone-called-in complaints, or before the ticket-
 * register bridge is wired up) and can forward/resolve any complaint, not just their own. */
export default function Complaints() {
  const session = useSession();
  const nav = useNavigate();
  const complaints = useAsync(() => complaintsApi.list(), [], 'complaints');
  const operators = useAsync(() => complaintsApi.operators(), [], 'complaintOperators');
  const materials = useAsync(() => complaintsApi.materials(), [], 'materials');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  // Claim/forward/resolve apply here immediately so the card updates the instant the server
  // confirms — not on whenever the next background list-refetch happens to land (Apps Script is
  // slow enough that a stale in-flight fetch can otherwise race a fresher one and win).
  const [overlay, setOverlay] = useState<Record<string, Partial<Row>>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  function patchComplaint(id: string, patch: Partial<Row>) {
    setOverlay((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
  }
  function markDeleted(id: string) {
    setDeletedIds((s) => new Set(s).add(id));
  }

  const rows = (complaints.data?.rows ?? [])
    .filter((r) => !deletedIds.has(String(r['Complaint ID'])))
    .map((r) => {
      const id = String(r['Complaint ID']);
      return overlay[id] ? { ...r, ...overlay[id] } : r;
    });
  const shown = status === 'all' ? rows : rows.filter((r) => String(r['Status']) === status);
  const counts: Record<StatusFilter, number> = {
    all: rows.length,
    open: rows.filter((r) => r['Status'] === 'open').length,
    in_progress: rows.filter((r) => r['Status'] === 'in_progress').length,
    paused: rows.filter((r) => r['Status'] === 'paused').length,
    resolved: rows.filter((r) => r['Status'] === 'resolved').length,
  };
  const isAdmin = session?.role === 'admin';

  return (
    <div className={isAdmin ? 'app' : 'app has-bottomnav'} style={isAdmin ? { maxWidth: 720 } : undefined}>
      {isAdmin && (
        <button type="button" className="ghost" style={{ width: 'auto', padding: '4px 0', marginBottom: 6 }} onClick={() => nav('/admin')}>
          ← Admin
        </button>
      )}
      <div className="spread">
        <h1>Complaints</h1>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="secondary sm" onClick={() => setMaterialsOpen(true)}>Materials…</button>
            <button type="button" className="sm" onClick={() => setAddOpen(true)}>+ Add</button>
          </div>
        )}
      </div>
      <p className="muted tiny" style={{ marginTop: 2 }}>
        Electrical maintenance tickets — claim one to start, forward it if you can&apos;t finish it.
      </p>
      <Banner error={complaints.error} />

      <div className="choice-group" style={{ margin: '10px 0 14px' }}>
        {(['all', 'open', 'in_progress', 'paused', 'resolved'] as StatusFilter[]).map((s) => (
          <button key={s} type="button" className={`choice-btn ${status === s ? 'on' : ''}`} onClick={() => setStatus(s)}>
            {s === 'all' ? 'All' : STATUS_LABEL[s]} ({counts[s]})
          </button>
        ))}
      </div>

      {complaints.loading && <div className="spinner" />}
      {!complaints.loading && shown.length === 0 && <Empty icon="📋">No complaints here.</Empty>}
      <div className="cardlist">
        {shown.map((r) => (
          <ComplaintCard
            key={String(r['Complaint ID'])} r={r} myId={session?.operatorId ?? ''} myName={session?.operatorName ?? ''} isAdmin={isAdmin}
            operators={operators.data?.rows ?? []} materials={materials.data?.rows ?? []} reload={complaints.reload}
            onPatch={(patch) => patchComplaint(String(r['Complaint ID']), patch)}
            onDeleted={() => { markDeleted(String(r['Complaint ID'])); complaints.reload(); }}
          />
        ))}
      </div>

      {addOpen && (
        <AddComplaintModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); complaints.reload(); }} />
      )}
      {materialsOpen && <MaterialsModal onClose={() => setMaterialsOpen(false)} onChanged={materials.reload} />}
      {!isAdmin && <BottomNav />}
    </div>
  );
}

function ComplaintCard({ r, myId, myName, isAdmin, operators, materials, reload, onPatch, onDeleted }: {
  r: Row; myId: string; myName: string; isAdmin: boolean; operators: LiteOperator[]; materials: MaterialRow[]; reload: () => void;
  onPatch: (patch: Partial<Row>) => void; onDeleted: () => void;
}) {
  const id = String(r['Complaint ID']);
  const status = String(r['Status']);
  const assignedTo = String(r['Assigned To'] ?? '');
  const assignedToName = String(r['Assigned To Name'] ?? '');
  const mine = assignedTo === myId;
  const canAct = status !== 'resolved' && (isAdmin || !assignedTo || mine);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const timeline = useAsync(
    () => (showTimeline ? complaintsApi.timeline(id) : Promise.resolve({ ok: true as const, rows: [] })),
    [showTimeline],
  );

  async function claim() {
    setBusy(true); setMsg('');
    try {
      await complaintsApi.claim(id);
      onPatch({ Status: 'in_progress', 'Assigned To': myId, 'Assigned To Name': myName });
      reload();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not claim.'); }
    finally { setBusy(false); }
  }

  async function resume() {
    setBusy(true); setMsg('');
    try {
      await complaintsApi.resume(id);
      onPatch({ Status: 'in_progress', 'Paused Reason': '' });
      reload();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not resume.'); }
    finally { setBusy(false); }
  }

  const ticketNo = r['Source'] === 'external' ? ` (Ticket #${r['Source Ticket No']})` : '';
  const where = [r['Department'], r['Block'] ? `Block ${r['Block']}` : '', r['Location']].filter(Boolean).join(' · ');

  return (
    <div className="item">
      <div className={`stripe ${STATUS_TONE[status] ?? ''}`} />
      <div className="body">
        <div className="title">
          {String(r['Details'] ?? '')}
          <span className="muted" style={{ fontWeight: 500, fontSize: '.8rem', marginLeft: 6 }}>
            #{id}{ticketNo}
          </span>
        </div>
        <div className="meta">
          <span className={`pill ${STATUS_TONE[status] ?? ''}`}>{STATUS_LABEL[status] ?? status}</span>
          {where && <span>{where}</span>}
          <span>{ago(String(r['Created At'] ?? '') || null)}</span>
          {r['Complainer Name'] ? <span>{String(r['Complainer Name'])}{r['Extension No'] ? ` · ext ${r['Extension No']}` : ''}</span> : null}
        </div>

        {status !== 'open' && status !== 'resolved' && assignedTo && (
          <p className="tiny" style={{ margin: '6px 0 0' }}>
            {mine ? <strong>Assigned to you</strong> : <>Being handled by <strong>{assignedToName || assignedTo}</strong></>}
          </p>
        )}
        {status === 'paused' && (
          <p className="tiny" style={{ margin: '4px 0 0', color: 'var(--warn)' }}>
            Waiting on material{r['Paused Reason'] ? <>: <strong>{String(r['Paused Reason'])}</strong></> : ''}
          </p>
        )}
        {status === 'resolved' && (materialsUsedLines(r).length > 0 || condemnedLines(r).length > 0) ? (
          <p className="tiny" style={{ margin: '6px 0 0' }}>
            {materialsUsedLines(r).length > 0 ? <>Used: <strong>{fmtMaterialLines(materialsUsedLines(r))}</strong></> : null}
            {materialsUsedLines(r).length > 0 && condemnedLines(r).length > 0 ? '  ·  ' : ''}
            {condemnedLines(r).length > 0 ? <>Condemned: <strong>{fmtMaterialLines(condemnedLines(r))}</strong></> : null}
          </p>
        ) : null}

        <Banner error={msg} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {status === 'open' && (
            <button type="button" className="sm" disabled={busy} onClick={claim}>Claim</button>
          )}
          {canAct && status === 'in_progress' && (
            <>
              <button type="button" className="secondary sm" disabled={busy} onClick={() => setShowPause(true)}>Pause…</button>
              <button type="button" className="secondary sm" disabled={busy} onClick={() => setShowForward(true)}>Forward…</button>
              <button type="button" className="sm" disabled={busy} onClick={() => setShowResolve(true)}>Resolve…</button>
            </>
          )}
          {canAct && status === 'paused' && (
            <>
              <button type="button" className="sm" disabled={busy} onClick={resume}>Resume</button>
              <button type="button" className="secondary sm" disabled={busy} onClick={() => setShowForward(true)}>Forward…</button>
              <button type="button" className="secondary sm" disabled={busy} onClick={() => setShowResolve(true)}>Resolve…</button>
            </>
          )}
          <button type="button" className="ghost sm" onClick={() => setShowTimeline((v) => !v)}>
            {showTimeline ? 'Hide timeline' : 'Timeline'}
          </button>
          <button
            type="button" className="ghost sm"
            onClick={async () => {
              setMsg('');
              try {
                const how = await shareComplaint(r, id);
                if (how === 'copied') setMsg('Copied to clipboard (share not supported on this browser).');
              } catch (e) { if ((e as Error)?.name !== 'AbortError') setMsg(e instanceof Error ? e.message : 'Could not share.'); }
            }}
          >
            📤 Share
          </button>
          {isAdmin && (
            <button
              type="button" className="ghost sm" style={{ color: 'var(--bad)', marginLeft: 'auto' }}
              disabled={busy}
              onClick={async () => {
                if (!window.confirm('Delete this complaint? This cannot be undone.')) return;
                setBusy(true); setMsg('');
                try { await complaintsApi.adminDelete(id); onDeleted(); }
                catch (e) { setMsg(e instanceof Error ? e.message : 'Could not delete.'); setBusy(false); }
              }}
            >
              Delete
            </button>
          )}
        </div>

        {showTimeline && (
          <div className="kv" style={{ gridTemplateColumns: '1fr', marginTop: 8 }}>
            {timeline.loading && <p className="muted tiny">Loading…</p>}
            {!timeline.loading && (timeline.data?.rows ?? []).map((ev) => (
              <div key={String(ev['Event ID'])}>
                <span className="kk">{fmtDateTime(String(ev['Timestamp'] ?? ''))}</span>{' '}
                <TimelineLine ev={ev} />
              </div>
            ))}
          </div>
        )}
      </div>

      {showPause && (
        <PauseModal
          complaintId={id}
          onClose={() => setShowPause(false)}
          onPaused={(reason) => {
            setShowPause(false);
            onPatch({ Status: 'paused', 'Paused Reason': reason });
            reload();
          }}
        />
      )}

      {showForward && (
        <ForwardModal
          complaintId={id} myId={myId} operators={operators}
          onClose={() => setShowForward(false)}
          onSent={(toId, toName) => {
            setShowForward(false);
            onPatch({ Status: 'in_progress', 'Assigned To': toId, 'Assigned To Name': toName });
            reload();
          }}
        />
      )}

      {showResolve && (
        <ResolveModal
          complaintId={id} materials={materials}
          onClose={() => setShowResolve(false)}
          onDone={(patch) => {
            setShowResolve(false);
            onPatch({ Status: 'resolved', ...patch });
            reload();
          }}
        />
      )}
    </div>
  );
}

function TimelineLine({ ev }: { ev: Row }) {
  const type = String(ev['Type']);
  const actor = String(ev['Actor Name'] ?? '') || 'Ticket register';
  const to = String(ev['To Operator Name'] ?? '');
  const from = String(ev['From Operator Name'] ?? '');
  const note = String(ev['Note'] ?? '');
  let line = '';
  if (type === 'created') line = `Created (${note || actor})`;
  else if (type === 'assigned') line = `${to} started working on it`;
  else if (type === 'forwarded') line = `${from} forwarded to ${to}${note ? ` — "${note}"` : ''}`;
  else if (type === 'paused') line = `${actor} paused it${note ? ` — waiting on ${note}` : ''}`;
  else if (type === 'resumed') line = `${actor} resumed it`;
  else if (type === 'resolved') line = `${actor} resolved it${note ? ` — "${note}"` : ''}`;
  else line = type;
  return <span>{line}</span>;
}

function PauseModal({ complaintId, onClose, onPaused }: {
  complaintId: string; onClose: () => void; onPaused: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function send() {
    setBusy(true); setErr('');
    try {
      await complaintsApi.pause(complaintId, reason.trim());
      onPaused(reason.trim());
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not pause.'); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title="Pause complaint"
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" onClick={send} disabled={busy}>{busy ? 'Saving…' : 'Pause'}</button>
      </>}
    >
      <p className="muted tiny" style={{ marginTop: 0 }}>
        For when you&apos;re blocked waiting on a part — stays assigned to you, just flagged so it&apos;s clear why it&apos;s stalled.
      </p>
      <label>What are you waiting on? (optional)</label>
      <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. 20W LED bulbs — none in stock" />
      <Banner error={err} />
    </Modal>
  );
}

function ForwardModal({ complaintId, myId, operators, onClose, onSent }: {
  complaintId: string; myId: string; operators: LiteOperator[]; onClose: () => void;
  onSent: (toId: string, toName: string) => void;
}) {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const choices = operators.filter((o) => o.operatorId !== myId);

  async function send() {
    if (!to) { setErr('Pick who to forward to.'); return; }
    setBusy(true); setErr('');
    try {
      await complaintsApi.forward(complaintId, to, note);
      onSent(to, choices.find((o) => o.operatorId === to)?.name ?? to);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not forward.'); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title="Forward complaint"
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Forward'}</button>
      </>}
    >
      <Banner error={err} />
      <label>Forward to</label>
      <select value={to} onChange={(e) => setTo(e.target.value)}>
        <option value="">Choose an operator…</option>
        {choices.map((o) => <option key={o.operatorId} value={o.operatorId}>{o.name} ({o.role})</option>)}
      </select>
      <label>Note (optional)</label>
      <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you're forwarding this…" />
    </Modal>
  );
}

type LineState = { item: string; qty: string };
const emptyLine = (): LineState => ({ item: '', qty: '1' });

/** One item+qty row: a free-text/select toggle for the item, a narrow qty box, and a remove
 * button — all on one line so rows stay compact and line up cleanly when stacked. */
function MaterialLineRow({ placeholder, materials, value, onChange, qty, onQtyChange, removable, onRemove }: {
  placeholder: string; materials: MaterialRow[]; value: string; onChange: (v: string) => void;
  qty: string; onQtyChange: (v: string) => void; removable: boolean; onRemove: () => void;
}) {
  const known = materials.some((m) => m.name === value);
  const [useOther, setUseOther] = useState(value !== '' && !known);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {useOther || materials.length === 0 ? (
          <input style={{ margin: 0 }} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        ) : (
          <select
            style={{ margin: 0 }}
            value={known ? value : ''}
            onChange={(e) => {
              if (e.target.value === '__other__') { setUseOther(true); onChange(''); }
              else onChange(e.target.value);
            }}
          >
            <option value="">None</option>
            {materials.filter((m) => m.active || m.name === value).map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
            <option value="__other__">Other…</option>
          </select>
        )}
      </div>
      {useOther && materials.length > 0 && (
        <button
          type="button" className="ghost sm" style={{ width: 'auto', flex: 'none', padding: '0 6px' }}
          title="Pick from list instead" aria-label="Pick from list instead"
          onClick={() => { setUseOther(false); onChange(''); }}
        >
          ☰
        </button>
      )}
      <input
        style={{ margin: 0, width: 56, flex: 'none', padding: '12px 6px', textAlign: 'center' }}
        inputMode="numeric" value={qty} onChange={(e) => onQtyChange(e.target.value)} placeholder="Qty"
      />
      <button
        type="button" className="ghost sm" style={{ width: 'auto', flex: 'none', color: 'var(--bad)' }}
        aria-label="Remove row" disabled={!removable}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}

/** A repeatable list of item+qty rows — a complaint can need several materials or condemn
 * several faulty parts in one visit, not just one of each. */
function MaterialLinesEditor({ label, placeholder, materials, lines, onChange }: {
  label: string; placeholder: string; materials: MaterialRow[]; lines: LineState[]; onChange: (lines: LineState[]) => void;
}) {
  return (
    <div>
      <label>{label}</label>
      {lines.map((line, i) => (
        <MaterialLineRow
          key={i} placeholder={placeholder} materials={materials}
          value={line.item} onChange={(v) => onChange(lines.map((l, idx) => (idx === i ? { ...l, item: v } : l)))}
          qty={line.qty} onQtyChange={(v) => onChange(lines.map((l, idx) => (idx === i ? { ...l, qty: v } : l)))}
          removable={lines.length > 1}
          onRemove={() => onChange(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : [emptyLine()])}
        />
      ))}
      <button type="button" className="secondary sm" style={{ width: 'auto' }} onClick={() => onChange([...lines, emptyLine()])}>
        + Add another
      </button>
    </div>
  );
}

function ResolveModal({ complaintId, materials, onClose, onDone }: {
  complaintId: string; materials: MaterialRow[]; onClose: () => void; onDone: (patch: Partial<Row>) => void;
}) {
  const [materialsUsed, setMaterialsUsed] = useState<LineState[]>([emptyLine()]);
  const [condemnedItems, setCondemnedItems] = useState<LineState[]>([emptyLine()]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const clean = (lines: LineState[]): MaterialLine[] => lines
    .filter((l) => l.item.trim())
    .map((l) => ({ item: l.item.trim(), qty: l.qty.trim() ? Number(l.qty) : '' }));

  async function send() {
    setBusy(true); setErr('');
    const mLines = clean(materialsUsed);
    const cLines = clean(condemnedItems);
    try {
      await complaintsApi.resolve(complaintId, { note, materialsUsed: mLines, condemnedItems: cLines });
      onDone({ 'Materials Used': mLines, 'Condemned Items': cLines });
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not resolve.'); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title="Resolve complaint"
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" onClick={send} disabled={busy}>{busy ? 'Saving…' : 'Mark resolved'}</button>
      </>}
    >
      <Banner error={err} />
      <p className="muted tiny" style={{ marginTop: 0 }}>
        Record everything installed or removed — kept for the department's stock record.
      </p>
      <MaterialLinesEditor
        label="Materials used (optional)" placeholder="e.g. 20W LED Light" materials={materials}
        lines={materialsUsed} onChange={setMaterialsUsed}
      />
      <MaterialLinesEditor
        label="Condemned items (optional)" placeholder="e.g. old 20W light (faulty)" materials={materials}
        lines={condemnedItems} onChange={setCondemnedItems}
      />
      <label>Note (optional)</label>
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything else worth recording…" />
    </Modal>
  );
}

function MaterialsModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const materials = useAsync(() => complaintsApi.materials(), [], 'materials');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function add() {
    if (!name.trim()) return;
    setBusy(true); setErr('');
    try { await complaintsApi.adminSaveMaterial(name.trim()); setName(''); materials.reload(); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not add.'); }
    finally { setBusy(false); }
  }
  async function remove(n: string) {
    if (!window.confirm(`Remove "${n}" from the list?`)) return;
    setBusy(true); setErr('');
    try { await complaintsApi.adminDeleteMaterial(n); materials.reload(); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove.'); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Materials list" onClose={onClose} footer={<button type="button" onClick={onClose}>Done</button>}>
      <Banner error={err} />
      <p className="muted tiny" style={{ marginTop: 0 }}>
        Operators pick from this list when resolving a complaint — keeps stock records consistent.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 20W LED Light"
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <button type="button" className="sm" style={{ width: 'auto' }} disabled={busy} onClick={add}>Add</button>
      </div>
      {materials.loading && <p className="muted tiny">Loading…</p>}
      {!materials.loading && (materials.data?.rows ?? []).length === 0 && (
        <p className="muted tiny">No materials yet — add your most-used items above.</p>
      )}
      {(materials.data?.rows ?? []).map((m) => (
        <div key={m.name} className="row">
          <span className="k">{m.name}</span>
          <button type="button" className="ghost sm" style={{ color: 'var(--bad)' }} disabled={busy} onClick={() => remove(m.name)}>Remove</button>
        </div>
      ))}
    </Modal>
  );
}

function AddComplaintModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ complaintNo: '', details: '', department: '', block: '', location: '', extensionNo: '', complainerName: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Stable for the life of this modal (not per click) so retrying after a transient error — or the
  // client's own automatic retry-on-slow-response — can never create a second row for one Save.
  // A manually-typed complaint no overrides this and becomes the row id directly.
  const clientId = useRef(`M${crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase()}`);

  async function save() {
    if (!form.details.trim()) { setErr('Details are required.'); return; }
    setBusy(true); setErr('');
    const { complaintNo, ...rest } = form;
    try { await complaintsApi.adminAdd({ ...rest, clientId: complaintNo.trim() ? complaintNo.trim().toUpperCase() : clientId.current }); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title="Add complaint"
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}
    >
      <Banner error={err} />
      <label>Complaint no (optional)</label>
      <input
        value={form.complaintNo} onChange={(e) => setForm({ ...form, complaintNo: e.target.value })}
        placeholder="auto-generated if left blank"
      />
      <label>Details</label>
      <textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Ceiling fan not working" />
      <div className="grid3">
        <div><label>Department</label><input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="CT SCAN" /></div>
        <div><label>Block</label><input value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} placeholder="A" /></div>
        <div><label>Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Cot no. 1" /></div>
      </div>
      <div className="grid3">
        <div><label>Extension no</label><input value={form.extensionNo} onChange={(e) => setForm({ ...form, extensionNo: e.target.value })} /></div>
        <div style={{ gridColumn: 'span 2' }}><label>Complainer name</label><input value={form.complainerName} onChange={(e) => setForm({ ...form, complainerName: e.target.value })} /></div>
      </div>
    </Modal>
  );
}
